import { useMemo, useState } from 'react';
import { Card, CopyButton, Field, Checkbox } from '../ui.tsx';

/** Painel de incorporação: URL pública, iframe, script e parâmetros. */
export function VideoEmbedPanel({
  publicId, published, aspectRatio, dimensions, startMode,
}: {
  publicId: string; published: boolean; aspectRatio: string;
  dimensions: { width: number | null; height: number | null } | null;
  startMode: 'bigplay' | 'vsl';
}): JSX.Element {
  const [autoplay, setAutoplay] = useState(false);
  const [muted, setMuted] = useState(true);
  const [responsive, setResponsive] = useState(true);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('480');
  const [pageId, setPageId] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');

  const origin = window.location.origin;

  // As dimensões reais do arquivo ganham da configuração: o espaço reservado
  // já nasce do tamanho certo e a página não pula quando o player carrega.
  const padding = useMemo(() => {
    if (dimensions?.width && dimensions.height) {
      return `${((dimensions.height / dimensions.width) * 100).toFixed(2)}%`;
    }
    switch (aspectRatio) {
      case '9:16': return '177.78%';
      case '1:1': return '100%';
      case '4:5': return '125%';
      default: return '56.25%';
    }
  }, [aspectRatio, dimensions]);

  const playerUrl = useMemo(() => {
    const url = new URL(`${origin}/v/${publicId}`);
    if (autoplay) url.searchParams.set('autoplay', '1');
    if (muted) url.searchParams.set('muted', '1');
    if (theme !== 'dark') url.searchParams.set('theme', theme);
    if (pageId) url.searchParams.set('page_id', pageId);
    if (utmSource) url.searchParams.set('utm_source', utmSource);
    if (utmCampaign) url.searchParams.set('utm_campaign', utmCampaign);
    return url.toString();
  }, [origin, publicId, autoplay, muted, theme, pageId, utmSource, utmCampaign]);

  const iframeCode = responsive
    ? `<div style="position:relative;width:${width};padding-top:${padding};background:#000;">
  <iframe
    src="${playerUrl}"
    style="position:absolute;inset:0;width:100%;height:100%;border:0;"
    allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
    allowfullscreen loading="eager"
    referrerpolicy="strict-origin-when-cross-origin"></iframe>
</div>`
    : `<iframe src="${playerUrl}" width="${width}" height="${height}" style="border:0;"
  allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="eager"></iframe>`;

  const scriptCode = `<div id="orbs-${publicId}" style="background:#000;"></div>
<script
  src="${origin}/embed/${publicId}.js"
  data-orbs-video="${publicId}"
  data-orbs-target="orbs-${publicId}"
  data-orbs-ratio="${padding}"${autoplay ? '\n  data-orbs-autoplay="1"' : ''}${muted ? '\n  data-orbs-muted="1"' : ''}${theme !== 'dark' ? `\n  data-orbs-theme="${theme}"` : ''}${pageId ? `\n  data-orbs-page-id="${pageId}"` : ''}
  async></script>`;

  const headCode = `<link rel="preconnect" href="${origin}" crossorigin>
<link rel="dns-prefetch" href="${origin}">
<link rel="preload" href="${origin}/embed/${publicId}.js" as="script">`;

  return (
    <div className="grid" style={{ gap: 18 }}>
      {!published && (
        <div className="alert warn">
          <span>!</span>
          <div>
            Este vídeo ainda não está publicado. O embed só funcionará em páginas externas depois da publicação —
            o player público responde 404 enquanto o vídeo estiver em rascunho.
          </div>
        </div>
      )}

      <Card title="Opções do embed" subtitle="As mudanças refletem no código e na prévia em tempo real.">
        <div className="grid cols-3">
          <Field label="Tema" id="em-theme">
            <select id="em-theme" value={theme} onChange={(e) => setTheme(e.target.value as 'dark' | 'light')}>
              <option value="dark">Escuro</option>
              <option value="light">Claro</option>
            </select>
          </Field>
          <Field label="Largura" id="em-w"><input id="em-w" value={width} onChange={(e) => setWidth(e.target.value)} /></Field>
          <Field label="Altura (modo fixo)" id="em-h">
            <input id="em-h" value={height} onChange={(e) => setHeight(e.target.value)} disabled={responsive} />
          </Field>
        </div>
        <div className="row">
          <Checkbox checked={responsive} onChange={setResponsive} label="Responsivo (mantém a proporção)" />
          <Checkbox checked={autoplay} onChange={setAutoplay} label="Autoplay" />
          <Checkbox checked={muted} onChange={setMuted} label="Iniciar mudo" />
        </div>
        <div className="grid cols-3">
          <Field label="ID da página" id="em-page" hint="Identifica onde o player está incorporado.">
            <input id="em-page" value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="lp-oferta-principal" />
          </Field>
          <Field label="utm_source" id="em-src"><input id="em-src" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} /></Field>
          <Field label="utm_campaign" id="em-camp"><input id="em-camp" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} /></Field>
        </div>
        <p className="card-sub">
          O script lê automaticamente os UTMs da URL da página que hospeda o player, inclusive
          <code> campaign.id</code>, <code>adset.id</code>, <code>ad.id</code> e <code>sck</code>. Os campos acima só
          servem para forçar valores fixos.
        </p>
      </Card>

      <Card
        title="URL pública"
        actions={<CopyButton value={playerUrl} />}
      >
        <div className="code-block">{playerUrl}</div>
        <a className="btn secondary small" style={{ marginTop: 12 }} href={playerUrl} target="_blank" rel="noreferrer">
          Abrir em nova aba
        </a>
      </Card>

      <Card title="Incorporar com iframe" subtitle="Funciona em qualquer site, sem expor credenciais." actions={<CopyButton value={iframeCode} />}>
        <div className="code-block">{iframeCode}</div>
      </Card>

      <Card
        title="Trecho para o <head> (opcional, recomendado)"
        subtitle="Cole no <head> da página. Resolve o DNS, abre a conexão e baixa o loader antes de o HTML chegar ao vídeo."
        actions={<CopyButton value={headCode} />}
      >
        <div className="code-block">{headCode}</div>
        <p className="hint" style={{ marginTop: 10 }}>
          Ganho real em conexões móveis: a página do cliente já chega ao embed com a conexão aberta.
          {startMode === 'vsl' && ' No modo VSL o vídeo começa sozinho, então os parâmetros de autoplay abaixo não fazem diferença.'}
        </p>
      </Card>

      <Card
        title="Incorporar com script"
        subtitle="Repassa os parâmetros de campanha da página automaticamente e ajusta a altura em vídeos verticais."
        actions={<CopyButton value={scriptCode} />}
      >
        <div className="code-block">{scriptCode}</div>
      </Card>

      <Card title="Prévia do embed" subtitle="Exatamente o que o visitante verá.">
        <div className={`player-frame ${aspectRatio === '9:16' ? 'vertical' : ''}`}>
          {published ? (
            <iframe
              src={playerUrl}
              title="Prévia do player"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--text-3)' }}>
              Publique o vídeo para ver a prévia pública.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
